/**
 * New Relic browser agent adapter.
 *
 * Handles NR-specific concerns: SPA route naming, PageActions, and user
 * identity. Spans are owned by the orchestrator via spans.js and
 * auto-harvested by NR when capture_measures is enabled.
 *
 * Deliberately does NOT report errors — see the jserrors note in init().
 *
 * Route changes use setCurrentRouteName() which lets NR automatically track
 * BrowserInteraction events with previousRouteName / targetRouteName attributes.
 */

import { BrowserAgent } from "@newrelic/browser-agent/loaders/browser-agent";
import {
  ENVIRONMENT,
  MAIN_APP_URL,
  HUMANLY_MAIN_APP_URL,
  NEWRELIC_ACCOUNT_ID,
  NEWRELIC_APPLICATION_ID,
  NEWRELIC_LICENSE_KEY,
  NEWRELIC_TRUST_KEY,
} from "@/common/Env";

let _agent = null;

function init() {
  _agent = new BrowserAgent({
    info: {
      applicationID: NEWRELIC_APPLICATION_ID,
      beacon: "bam.nr-data.net",
      errorBeacon: "bam.nr-data.net",
      licenseKey: NEWRELIC_LICENSE_KEY,
      sa: 1,
      jsAttributes: { "deploy.env": ENVIRONMENT },
    },
    init: {
      ajax: {
        deny_list: ["bam.nr-data.net"],
      },
      browser_consent_mode: {
        enabled: false,
      },
      distributed_tracing: {
        enabled: true,
        allowed_origins: [MAIN_APP_URL, HUMANLY_MAIN_APP_URL],
        exclude_newrelic_header: true,
      },
      // We don't use New Relic for frontend errors. This defaults to enabled and
      // captures uncaught errors + unhandled rejections on its own, so leaving it
      // out is not the same as opting out.
      jserrors: {
        enabled: false,
      },
      performance: {
        capture_detail: true,
        capture_marks: true,
        capture_measures: true,
      },
      logging: {
        enabled: false,
      },
      privacy: {
        cookies_enabled: true,
      },
    },
    loader_config: {
      accountID: NEWRELIC_ACCOUNT_ID,
      agentID: NEWRELIC_APPLICATION_ID,
      applicationID: NEWRELIC_APPLICATION_ID,
      licenseKey: NEWRELIC_LICENSE_KEY,
      trustKey: NEWRELIC_TRUST_KEY,
    },
  });
}

const setUser = (user, team) => {
  if (!_agent) return;
  if (!user) {
    _agent.setUserId(null);
    _agent.setCustomAttribute("team.id", null);
    _agent.setCustomAttribute("team.name", null);
    return;
  }
  _agent.setUserId(String(user.id));
  _agent.setCustomAttribute("team.id", String(team.id));
  _agent.setCustomAttribute("team.name", team.name);
};

const startNavigation = (name) => {
  if (!_agent) return;
  _agent.setCurrentRouteName(name);
};

const endNavigation = () => {
  // Route naming is handled by setCurrentRouteName; no manual interaction to close.
};

const addEvent = (name, attrs = {}) => {
  if (!_agent) return;
  const eventAttrs = {};
  Object.entries(attrs).forEach(([k, v]) => {
    if (v !== undefined && v !== null) eventAttrs[k] = v;
  });
  _agent.addPageAction(name, eventAttrs);
};

export default { init, setUser, startNavigation, endNavigation, addEvent };
